import type {Digit, Options} from '@pb/kit-formatters';
import {
    formatDate,
    DateFormats,
    NumberFormat,
    MoneyFormat,
    Digits,
    MONEY,
    FLOAT,
} from '@pb/kit-formatters';
import {DataTypes} from './constants';
import type {InputControlValue} from './typedef';
import {formatDateRange} from './utils/formatDateRange';

type Locale = {
    dateFormat: string,
    decimalFormat: string,
    thousandFormat: string,
    currency: string,
    symbolPosition: string,
};

export const getDisplayValue = (
    inputControlValue: InputControlValue,
    dataType: string,
    locale: Locale,
    options?: Options,

): {value: string, suffix?: string} => {
    if (
        inputControlValue === null ||
        inputControlValue === undefined ||
        inputControlValue === ''
    ) {
        return {value: ''};
    }

    const formatValue = (value: InputControlValue) => {
        const numberFormat = new NumberFormat(2, true, false, '');
        return numberFormat.format(Number(value), locale, {
            hideTrailingZeros: true,
            ...options,
        });
    };

    const getAmount = (value: unknown) => ({
        amount: value instanceof Object && 'amount' in value
            ? Number(value.amount)
            : 0,
        currency: locale.currency,
    });

    const getFormattedMoney = (
        value: unknown,
        fromDigit: Digit = Digits.ONE,
    ) => {
        return new MoneyFormat(FLOAT, fromDigit).format(
            getAmount(value),
            locale,
            {
                showDigit: false,
                hideSymbol: true,
                hideTrailingZeros: true,
                showZeros: true,
                fromDigit,
                preventScale: true,
                ...options,
            },
        );
    };

    const getCurrency = (value: unknown) => {
        return MONEY.format(getAmount(value), locale, {
            hideSymbol: true,
            showZeros: false,
            ...options,
        });
    };

    switch (dataType) {
        case DataTypes.MILLION:
            return {
                value: getFormattedMoney(inputControlValue, Digits.MILLION),
                suffix: Digits.MILLION,
            };

        case DataTypes.THOUSANDS:
            return {
                value: getFormattedMoney(inputControlValue, Digits.THOUSAND),
                suffix: Digits.THOUSAND,
            };

        case DataTypes.CURRENCY:
            return {
                value: getCurrency(inputControlValue),
            };

        case DataTypes.PERCENT:
            return {
                value: formatValue(inputControlValue),
                suffix: '%',
            };

        case DataTypes.MULTIPLES:
            return {
                value: formatValue(inputControlValue),
                suffix: 'x',
            };

        case DataTypes.DATE:
            return {
                value: formatDate(
                    (inputControlValue as (string | number)),
                    DateFormats[locale.dateFormat] || locale.dateFormat,
                ),
            };

        case DataTypes.DATE_RANGE: {
            const value = formatDateRange(
                inputControlValue as string,
                locale.dateFormat,
            ).value;

            return {
                value: value ?? '',
            };
        }

        case DataTypes.NUMBER:
            return {
                value: formatValue(inputControlValue),
            };

        default:
            return {
                value: String(inputControlValue),
            };
    }
};
